import React, { useMemo, useRef, useState,useEffect } from 'react';
import PredictiveSearch from './shopifyPredictiveSearch';

import styled from 'styled-components';
import {useCart} from '@shopify/hydrogen-react';
import CartDetails from './CartDetails';
import TaxButton from './taxButton';

import ConvertOrderButton from './ConvertOrderButton.js';
import { createTrainingSearchSessionId, sendTrainingOptionEvent } from './posTrainingEvents.js';

const TrainingWorkspace = styled.div`
  min-height: 100vh;
  background: #f6f7f4;
  color: #1f2933;
  padding: 16px;
  box-sizing: border-box;
  text-align: left;

  @media (max-width: 760px) {
    padding: 10px;
  }
`;

const CommandBar = styled.header`
  display: grid;
  grid-template-columns: minmax(220px, 320px) 1fr;
  gap: 14px;
  align-items: stretch;
  margin-bottom: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TraineePanel = styled.div`
  background: #ffffff;
  border: 1px solid #d7ded6;
  border-radius: 8px;
  padding: 14px 16px;
  min-width: 0;
`;

const Kicker = styled.div`
  color: #5f6f65;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
`;

const WorkspaceTitle = styled.h2`
  margin: 4px 0 2px;
  color: #16251b;
  font-size: 1.22rem;
  line-height: 1.2;
`;

const WorkspaceMeta = styled.div`
  color: #667085;
  font-size: 0.88rem;
  line-height: 1.35;
`;

const SearchPanel = styled.div`
  background: #ffffff;
  border: 1px solid #d7ded6;
  border-radius: 8px;
  padding: 12px;
  display: grid;
  grid-template-columns: minmax(180px, 260px) 1fr;
  gap: 12px;
  align-items: end;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const FieldLabel = styled.span`
  color: #344054;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
`;

const InputBase = styled.input`
  width: 100%;
  min-height: 46px;
  border: 1px solid #c9d4cd;
  border-radius: 8px;
  background: #fbfcfb;
  color: #18251d;
  padding: 0 13px;
  font-size: 1rem;
  font-weight: 650;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: #227c68;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(34, 124, 104, 0.16);
  }

  &::placeholder {
    color: #8a968f;
    font-weight: 500;
  }
`;

const SearchInput = styled(InputBase)`
  font-size: 1.08rem;
`;

const WorkspaceGrid = styled.main`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 380px);
  gap: 14px;
  align-items: start;

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const SearchColumn = styled.section`
  min-width: 0;
`;

const EmptySearchPanel = styled.div`
  min-height: 280px;
  border: 1px dashed #b9c4be;
  border-radius: 8px;
  background: #ffffff;
  display: grid;
  place-items: center;
  color: #607069;
  text-align: center;
  padding: 28px;
  box-sizing: border-box;
`;

const EmptySearchTitle = styled.h3`
  margin: 0 0 6px;
  color: #283a31;
  font-size: 1.2rem;
`;

const EmptySearchText = styled.p`
  margin: 0;
  max-width: 360px;
  line-height: 1.45;
  font-size: 0.94rem;
`;

const EmptyOrderState = styled(EmptySearchPanel)`
  min-height: 170px;
  border-width: 0;
  border-radius: 0;
`;

const OrderRail = styled.aside`
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 1040px) {
    position: static;
  }
`;

const OrderPanel = styled.section`
  background: #ffffff;
  border: 1px solid #d7ded6;
  border-radius: 8px;
  overflow: hidden;
`;

const OrderPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border-bottom: 1px solid #e4e9e6;
  background: #f9faf8;
`;

const OrderPanelTitle = styled.h3`
  margin: 0;
  color: #18251d;
  font-size: 1rem;
`;

const OrderCount = styled.span`
  background: #e6f1ed;
  color: #1c6b5b;
  border: 1px solid #c9e2da;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
`;

const OrderActions = styled.div`
  padding: 12px 14px 14px;
  display: grid;
  gap: 10px;
  border-top: 1px solid #e4e9e6;
`;


// Fetch server time instead of using client's Date.now() to avoid timezone/clock issues
// Users in Philippines may have incorrect system clocks
const fetchServerTime = async () => {
  try {
    const response = await fetch("https://api.pacificpearltraining.site/serverTime");
    if (response.ok) {
      const data = await response.json();
      return data.timestamp; // Server returns { timestamp: Date.now() }
    }
  } catch (error) {
    console.error("Failed to fetch server time, falling back to client time:", error);
  }
  // Fallback to client time if server request fails
  return Date.now();
};

const POSComponent = ({customerTags,customerId}) => {

  const [updatedLastName, setUpdatedLastName] = useState('');

  const [draftOrderId, setDraftOrderId] = useState(null);

  const [lastNameButtonClickTimestamp, setLastNameButtonClickTimestamp] = useState(null);



  const handleDraftOrderIdChange = (id) => {
    setDraftOrderId(id);
    console.log(draftOrderId);
  };



  const cart = useCart();
  
  const {id,status, lines, note, cost, error: cartError} = cart;
  const primaryLineCount = useMemo(() => {
    return lines
      .filter(line => !line.attributes?.some(attribute => attribute.key === '_io_parent_order_group'))
      .reduce((count, line) => count + Number(line.quantity || 0), 0);
  }, [lines]);

  useEffect(() => {
    console.log('useCart id updated', id);
  }, [id]);

  useEffect(() => {
    console.log('POSComponent - useCart status', status);
  }, [status]);


  useEffect(() => {
    console.log('POSComponent - useCart cartError', cartError);
  }, [cartError]);

  useEffect(() => {
    console.log('POSComponent - lines changed:', lines);
  }, [lines]); // Dependency array with 'lines'


  // console.log('POSComponent ran');
  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const trainingSessionId = useMemo(() => createTrainingSearchSessionId(), []);
  const mainSearchStartedAtRef = useRef(null);
  const [mainSearchStartedAt, setMainSearchStartedAt] = useState(null);
  const [restaurantNameForTracking, setRestaurantNameForTracking] = useState('');


  const handleInputChange = (event) => {
    const nextSearchTerm = event.target.value;
    const trimmed = nextSearchTerm.trim();
    if (trimmed && !searchTerm.trim()) {
      const startedAt = Date.now();
      mainSearchStartedAtRef.current = startedAt;
      setMainSearchStartedAt(startedAt);
      sendTrainingOptionEvent({
        eventType: 'main_search_started',
        searchKind: 'main',
        sessionId: trainingSessionId,
        traineeId: customerId,
        restaurantName: restaurantNameForTracking,
        query: trimmed,
      });
    } else if (!trimmed) {
      mainSearchStartedAtRef.current = null;
      setMainSearchStartedAt(null);
    }
    setSearchTerm(nextSearchTerm);
  };


  const handleSuccessAddToCart = (value) => {
    setSearchTerm(value);
  };
  
  useEffect(() => {
    if (Array.isArray(customerTags) && customerTags.length > 0) {
      let tempVendorFilter = '';
      let trackingRestaurantName = '';
  
      customerTags.forEach(function(tag) {
        tag = tag.trim();
        if (tag === "No1WokRestaurant") {
          tag = "No1 Wok Restaurant";
        }
        if (tag.toLowerCase() !== "trainee" && !/\d/.test(tag)) {
          if (tempVendorFilter !== "") tempVendorFilter += " OR ";
          tempVendorFilter += tag; 
          if (!trackingRestaurantName) trackingRestaurantName = tag;
        }
      });
      
      setVendorFilter(tempVendorFilter);
      setRestaurantNameForTracking(trackingRestaurantName);
    }
  }, [customerTags]);


  const searchTermWithVendor = searchTerm ? vendorFilter + " " + searchTerm : '';

  // DEBUG: Log search terms
  console.log('🔍 DEBUG SEARCH:', {
    vendorFilter,
    searchTerm,
    searchTermWithVendor,
    customerTags
  });

  const handleLastNameChange = async (event) => {
    setUpdatedLastName(event.target.value);
    // Use server time to ensure correct timestamp regardless of user's local clock
    const serverTimestamp = await fetchServerTime();
    setLastNameButtonClickTimestamp(serverTimestamp);
  };
  
  const clearLastName = () => {
    setUpdatedLastName("");
  };
  

  return (
    <TrainingWorkspace>
      <CommandBar>
        <TraineePanel>
          <Kicker>Training POS</Kicker>
          <WorkspaceTitle>{restaurantNameForTracking || 'Restaurant menu'}</WorkspaceTitle>
          <WorkspaceMeta>
            {primaryLineCount > 0 ? `${primaryLineCount} item${primaryLineCount === 1 ? '' : 's'} in progress` : 'Ready for a practice order'}
          </WorkspaceMeta>
        </TraineePanel>

        <SearchPanel>
          <FieldGroup>
            <FieldLabel>Phone</FieldLabel>
            <InputBase
              type="text"
              value={updatedLastName}
              onChange={handleLastNameChange}
              placeholder="Customer phone"
              autoComplete="off"
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Find Item</FieldLabel>
            <SearchInput
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              placeholder="Dish name, number, or customer words"
              autoComplete="off"
              autoFocus
            />
          </FieldGroup>
        </SearchPanel>
      </CommandBar>

      <WorkspaceGrid>
        <SearchColumn>
          {searchTermWithVendor ? (
            <PredictiveSearch
              vendorFilter={vendorFilter}
              searchTerm={searchTermWithVendor}
              rawSearchTerm={searchTerm}
              handleSuccessAddToCart={handleSuccessAddToCart}
              trainingSessionId={trainingSessionId}
              traineeId={customerId}
              restaurantName={restaurantNameForTracking}
              mainSearchStartedAt={mainSearchStartedAt}
            />
          ) : (
            <EmptySearchPanel>
              <div>
                <EmptySearchTitle>Ready for the next item</EmptySearchTitle>
                <EmptySearchText>No active search.</EmptySearchText>
              </div>
            </EmptySearchPanel>
          )}
        </SearchColumn>

        <OrderRail>
          <OrderPanel>
            <OrderPanelHeader>
              <OrderPanelTitle>Current Order</OrderPanelTitle>
              <OrderCount>{primaryLineCount} item{primaryLineCount === 1 ? '' : 's'}</OrderCount>
            </OrderPanelHeader>
            {lines.length > 0 ? (
              <CartDetails lines={lines} cost={cost} />
            ) : (
              <EmptyOrderState>
                <div>
                  <EmptySearchTitle>No items yet</EmptySearchTitle>
                  <EmptySearchText>Cart is empty.</EmptySearchText>
                </div>
              </EmptyOrderState>
            )}
            {lines.length > 0 && (
              <OrderActions>
                <TaxButton customerId={customerId} lines={lines} cartNote={note} handleDraftOrderIdChange={handleDraftOrderIdChange} />
                {draftOrderId && (
                  <ConvertOrderButton
                    draftOrderId={draftOrderId}
                    handleDraftOrderIdChange={handleDraftOrderIdChange}
                    updatedLastName={updatedLastName}
                    clearLastName={clearLastName}
                    customerId={customerId}
                    lastNameButtonClickTimestamp={lastNameButtonClickTimestamp}
                  />
                )}
              </OrderActions>
            )}
          </OrderPanel>
        </OrderRail>
      </WorkspaceGrid>
    </TrainingWorkspace>
  );
};







export default POSComponent;
